import { useEffect, useMemo, useRef, useState } from "react";
import { Table, Select, Tooltip } from "antd";
import { UserCog, Users } from "lucide-react";

import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { Button } from "../../../components/ui/button";
import { WizardFooter, PRIMARY_BTN } from "./WizardFooter";
import {
    EMPLOYEE_MIGRATION_REPORTING_MANAGER,
    EMPLOYEE_PERSONAL_INFO,
} from "../../../constants/api";

const managerLabel = (e) =>
    `${e.first_name || ""} ${e.last_name || ""}${e.employee_id ? ` - ${e.employee_id}` : ""}`.trim();

const ReportingManagerStep = ({ migrationId, onDone }) => {
    const { fetch, update } = useApiMethods();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [unassigned, setUnassigned] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [assignment, setAssignment] = useState({}); // employeeId -> managerId
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [bulkManager, setBulkManager] = useState(null);

    const loadedRef = useRef(false);
    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        const load = async () => {
            setLoading(true);
            try {
                const [list, emps] = await Promise.all([
                    fetch(EMPLOYEE_MIGRATION_REPORTING_MANAGER, { id: migrationId }),
                    fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
                ]);
                if (list?.status === "success") setUnassigned(list.data || []);
                if (emps?.status === "success") setEmployees(emps.data || []);
            } catch {
                ToastMsg("error", "Could not load employees for reporting-manager assignment");
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [migrationId]);

    const managerOptions = useMemo(
        () => employees.map((e) => ({ value: e.id, label: managerLabel(e) })),
        [employees]
    );

    const setManager = (empId, managerId) =>
        setAssignment((prev) => ({ ...prev, [empId]: managerId || undefined }));

    const applyBulk = () => {
        if (!bulkManager || selectedKeys.length === 0) return;
        setAssignment((prev) => {
            const next = { ...prev };
            selectedKeys.forEach((k) => { next[k] = bulkManager; });
            return next;
        });
        setSelectedKeys([]);
        setBulkManager(null);
    };

    const assignedCount = Object.values(assignment).filter(Boolean).length;

    const save = async () => {
        const payload = Object.entries(assignment)
            .filter(([, m]) => m)
            .map(([id, reporting_manager_id]) => ({ id: Number(id), reporting_manager_id }));
        if (payload.length === 0) {
            ToastMsg("warning", "Assign at least one reporting manager, or skip.");
            return;
        }
        setSaving(true);
        try {
            const res = await update(EMPLOYEE_MIGRATION_REPORTING_MANAGER, payload);
            if (res?.status === "success") {
                ToastMsg("success", "Reporting managers assigned");
                onDone();
            } else {
                ToastMsg("error", res?.message || "Could not save assignments");
            }
        } catch {
            ToastMsg("error", "Something went wrong while saving");
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { title: "Code", dataIndex: "emp_code", key: "emp_code", width: 120 },
        {
            title: "Name",
            key: "name",
            render: (_, r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || "-",
        },
        { title: "Designation", dataIndex: ["designation", "name"], key: "designation", render: (_, r) => r.designation?.name || "-" },
        { title: "Department", dataIndex: ["department", "name"], key: "department", render: (_, r) => r.department?.name || "-" },
        {
            title: "Reporting Manager",
            key: "rm",
            width: 280,
            render: (_, r) => (
                <Select
                    showSearch
                    size="large"
                    className="w-full"
                    placeholder="Select manager"
                    value={assignment[r.id] || undefined}
                    onChange={(v) => setManager(r.id, v)}
                    optionFilterProp="label"
                    options={managerOptions}
                    allowClear
                />
            ),
        },
    ];

    return (
        <div>
            <div className="p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <UserCog className="w-4 h-4 text-sidebar-primary" />
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        Step 4 — Assign reporting managers
                    </h3>
                    <span className="text-xs text-muted-foreground">
                        ({assignedCount}/{unassigned.length} assigned)
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <Select
                        showSearch
                        size="large"
                        style={{ width: 240 }}
                        placeholder={`Bulk-assign ${selectedKeys.length} selected`}
                        value={bulkManager || undefined}
                        onChange={setBulkManager}
                        optionFilterProp="label"
                        options={managerOptions}
                        disabled={selectedKeys.length === 0}
                        allowClear
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={applyBulk}
                        disabled={!bulkManager || selectedKeys.length === 0}
                    >
                        Apply
                    </Button>
                </div>
            </div>

            {!loading && unassigned.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Users className="w-10 h-10 text-green-500 mb-3" />
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                        All migrated employees already have a reporting manager.
                    </p>
                </div>
            ) : (
                <Table
                    size="large"
                    loading={loading}
                    rowKey="id"
                    columns={columns}
                    dataSource={unassigned}
                    pagination={false}
                    scroll={{ y: "calc(100vh - 420px)" }}
                    rowSelection={{
                        selectedRowKeys: selectedKeys,
                        onChange: setSelectedKeys,
                    }}
                />
            )}
            </div>

            <WizardFooter>
                <Tooltip title="You can assign reporting managers later from the employee module">
                    <Button variant="outline" onClick={onDone} disabled={saving}>Finish later</Button>
                </Tooltip>
                <Button className={PRIMARY_BTN} onClick={save} disabled={saving || assignedCount === 0}>
                    {saving ? "Saving…" : "Save & Finish"}
                </Button>
            </WizardFooter>
        </div>
    );
};

export default ReportingManagerStep;
